import { inject, injectable } from 'inversify';
import 'reflect-metadata';

import {
  TYPES as ApiGatewayTypes,
  BatchPayload,
  BatchResponse,
  IApiGateway,
  isErrorResponse,
} from '@desygner/ui-core-api-gateway';

import { ICollaborateService } from './collaborate.interfaces';
import { Collaborator, Permission, Project, UserId, UserInfo } from './collaborate.types';

@injectable()
export class CollaborateService implements ICollaborateService {
  protected cache: Map<string, any> = new Map();

  constructor(
    @inject(ApiGatewayTypes.ApiGateway)
    protected readonly apiGateway: IApiGateway
  ) {}

  private get endpointBase() {
    return `brand/companies/${this.apiGateway.company}`;
  }

  private async fetchPermissions(projectId: number): Promise<Permission[]> {
    const response = await this.apiGateway.fetch(
      'GET',
      `${this.endpointBase}/designs/${projectId}/permissions`
    );

    if (isErrorResponse(response)) {
      return [];
    }

    return response.data as Permission[];
  }

  private async fetchUserDetails(userIds: number[]): Promise<UserInfo['user'][]> {
    const memberInfoDataRequest = userIds.map<BatchPayload>((id, index) => ({
      id: index,
      method: 'get',
      url: `${this.endpointBase}/memberships?u=${id}`,
    }));

    if (userIds.length === 0) {
      return [];
    }

    const batchData = await this.apiGateway.batch(memberInfoDataRequest);

    return batchData
      .filter((data) => {
        if (data.status !== 200) {
          return false;
        }

        const userInfo = JSON.parse(data.body) as UserInfo[];

        return !!userInfo && userInfo.length > 0;
      })
      .map<UserInfo['user']>((data) => {
        const userInfo = JSON.parse(data.body) as UserInfo[];
        const userProfile = userInfo[0].user;

        userProfile.profilePicture = userProfile.details?.find(
          (detail) => detail.type === 'profile_picture'
        )?.content;

        const originalEmail = userInfo[0].details.find(
          (detail) => detail.type === 'email_original'
        );

        userProfile.details = [
          ...userProfile.details.filter(
            (detail) => !['last_name', 'first_name'].includes(detail.type)
          ),
          ...userInfo[0].details.filter(
            (detail) => !['last_name', 'last_name'].includes(detail.type)
          ),
        ];

        if (originalEmail) {
          userProfile.details = [
            ...userProfile.details.filter((detail) => detail.type !== 'email'),
            {
              content: originalEmail.content,
              type: 'email',
            },
          ];
        }

        return userProfile;
      });
  }

  async getMembers({ projectId, encodedId }: Project): Promise<Collaborator[]> {
    const cacheKey = `members-${encodedId}-${projectId}`;

    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey) as Collaborator[];
    }

    const permissionData = await this.fetchPermissions(projectId);

    const userIds = permissionData.map((data) => data.user);
    const userInfo = await this.fetchUserDetails(userIds);

    const users = permissionData
      .map<Collaborator | undefined>((permission) => {
        const user = userInfo.find((member) => member.id === permission.user);

        if (user) {
          const email = user.details.find((detail) => detail.type === 'email');

          const lastName = user.details.find((detail) => detail.type === 'last_name')?.content;
          const firstName = user.details.find((detail) => detail.type === 'first_name')?.content;

          const fullName = [lastName, firstName].filter(Boolean).join(' ');

          const collaborator: Collaborator = {
            id: permission.user_id ?? user.id,
            permissionUserId: permission.user,
            name: fullName || user.nickname,
            email: email?.content ?? user.nickname,
            sourceUrl: user.profilePicture,
            permissionId: permission.id,
            permissionType: permission.type,
          };

          return collaborator;
        } else {
          return {
            id: permission.user,
            permissionUserId: permission.user,
            permissionId: permission.id,
            permissionType: permission.type,
            name: '',
            email: '',
          };
        }
      })
      .filter((member): member is Collaborator => member !== undefined);

    this.cache.set(cacheKey, users);

    return users;
  }

  /**
   * TODO: Implement this method
   * Invitation for Desygner is different than invitation for WeBrand
   * Desygner invitation is sent to an email
   * WeBrand invitation adds a user from workspace to the project
   */
  public addMembers(project: Project, userIds: UserId[]): Promise<BatchResponse[]> {
    this.cache.clear();
    const addUserDataRequest = userIds.map<BatchPayload>((id, index) => ({
      id: index,
      method: 'post',
      body: JSON.stringify({ user: id, type: 'shared' }),
      url: `${this.endpointBase}/designs/${project.projectId}/permissions`,
    }));

    return this.apiGateway.batch(addUserDataRequest);
  }

  async removeMembers(project: Project, users: Collaborator[]): Promise<void> {
    if (users.length === 0) {
      return;
    }

    const removeUserDataRequest = users.map<BatchPayload>((user, index) => ({
      id: index,
      method: 'delete',
      body: JSON.stringify({ user: user.id, type: user.permissionType }),
      url: `${this.endpointBase}/designs/${project.projectId}/permissions/${user.permissionId}`,
    }));

    await this.apiGateway.batch(removeUserDataRequest);
    this.cache.clear();
  }

  async searchMembers(
    keyword: string,
    status: number,
    limit: number,
    offset: number
  ): Promise<UserInfo[]> {
    const { data: response } = await this.apiGateway.fetch(
      'GET',
      `${this.endpointBase}/memberships?q=${keyword}&status=${status}&limit=${limit}&first=${offset}`
    );

    return (response || []) as UserInfo[];
  }
}
